import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Alert, Skeleton, useTheme, Typography, Stack, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import HeaderWithControls from "../../components/HeaderWithControls";
import SiteCard from "./SiteCard";
import { useAppContext } from "../../contexts/AppContext";
import { tokens } from "../../theme";
import HubIcon from "@mui/icons-material/Hub";
// Sidebar uses AccountTreeOutlinedIcon for the Client Sites route — mirror it
// here so the page header matches the sidebar entry.
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";

// Empty-state SVG illustration — federation map
const EmptySitesSvg = () => (
  <svg viewBox="0 0 320 200" width="240" height="150" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="empty-line" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#1B365D" stopOpacity="0" />
        <stop offset="50%" stopColor="#0087B4" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#1B365D" stopOpacity="0" />
      </linearGradient>
    </defs>
    <line x1="40" y1="100" x2="280" y2="100" stroke="url(#empty-line)" strokeWidth="1" />
    <line x1="160" y1="20" x2="160" y2="180" stroke="url(#empty-line)" strokeWidth="1" />
    {[[60,60],[260,60],[60,140],[260,140],[160,100]].map(([x,y],i)=>(
      <g key={i}>
        <circle cx={x} cy={y} r="18" fill="var(--fx-paper)" stroke="var(--fx-border-strong)" strokeWidth="1" />
        <circle cx={x} cy={y} r="5" fill="#0087B4" opacity={i===4?1:0.55} />
      </g>
    ))}
  </svg>
);

// HeaderWithControls renders these directly as <Select> children, so the
// entries must be <MenuItem> JSX, not plain {value,label} objects.
const SORT_OPTIONS = [
  <MenuItem key="display_name"    value="display_name">Name (A → Z)</MenuItem>,
  <MenuItem key="status"          value="status">Status (online first)</MenuItem>,
  <MenuItem key="supported_tasks" value="supported_tasks"># of supported tasks</MenuItem>,
];

const STATUS_RANK = { online: 0, maintenance: 1, unknown: 2, offline: 3 };

/**
 * Read-only directory of registered federation sites. Phase F1 ships this page
 * as a mock-backed overview; selection happens in the configs scene.
 *
 * Backend reality (per Decision 6 + Decision 9): GET /sites and
 * /sites/{id}/capabilities are not implemented yet — the AppContext loader
 * falls back to mock fixtures when VITE_SITES_USE_MOCK is not set to "false".
 */
const SitesScene = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const { sites, siteCapabilities, sitesLoading, sitesError, fetchSites } = useAppContext();

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("display_name");

  useEffect(() => {
    // Loader is also auto-triggered on auth-ready in AppContext; this is a
    // belt-and-braces fetch for the rare case the page mounts before that ran.
    if (!sites || sites.length === 0) {
      fetchSites?.();
    }
  }, [sites?.length, fetchSites]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const list = q
      ? (sites || []).filter((s) =>
          [s.display_name, s.organization_display_name, s.site_id]
            .filter(Boolean)
            .some((v) => v.toLowerCase().includes(q))
        )
      : sites || [];

    return [...list].sort((a, b) => {
      if (sortBy === "status") {
        return (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99);
      }
      if (sortBy === "supported_tasks") {
        return (b.supported_tasks?.length || 0) - (a.supported_tasks?.length || 0);
      }
      return (a.display_name || "").localeCompare(b.display_name || "");
    });
  }, [sites, searchQuery, sortBy]);

  return (
    <Box sx={{ p: 3, maxWidth: 1440, mx: 'auto' }}>
      <HeaderWithControls
        title={t("sites.title", "Client Sites")}
        subtitle={t(
          "sites.subtitle",
          "Registered participants in the federation. Each site contributes its local datasets to runs."
        )}
        icon={<AccountTreeOutlinedIcon />}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchPlaceholder="Search sites by name or organization…"
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOptions={SORT_OPTIONS}
      />

      {sitesError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Failed to load sites: {sitesError}
        </Alert>
      )}

      {sitesLoading ? (
        <Grid container spacing={2}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <Skeleton variant="rounded" height={180} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      ) : filtered.length === 0 ? (
        <Stack alignItems="center" sx={{ py: 6, color: colors.grey[500] }}>
          <EmptySitesSvg />
          <Typography sx={{ color: 'var(--fx-text-primary)', mt: 1.5, fontWeight: 600, fontSize: "0.9375rem" }}>
            No sites match your search.
          </Typography>
          <Typography sx={{ color: colors.grey[500], fontSize: "0.8125rem", mt: 0.5 }}>
            Try clearing the search box or refresh.
          </Typography>
        </Stack>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((site) => (
            <Grid item xs={12} sm={6} lg={4} key={site.site_id}>
              <SiteCard site={site} capabilities={siteCapabilities?.[site.site_id] || []} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SitesScene;
